// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Closing the player's microphone while their machine talks (M8-13).
//
// 07 §7's last mixing rule, and the only one that is not about a bus: "**while the machine speaks,
// the player's outgoing voice-chat microphone is briefly gated so other players never hear the
// machine talking through them** — the same fix Chess VR applied to its narration".
//
// WHAT GOES WRONG WITHOUT IT
// --------------------------
// The machine's callout comes out of the player's speakers, into the player's microphone, and back
// out of five other people's speakers — **half a second late and through a codec tuned for
// speech**. Everybody in the room can already hear that machine from its own bay, so what they get
// is the same line twice, offset, one copy smeared. It is worst on a Quest, whose speakers are
// open-air by design and point down at the wearer's ears with nothing in the way of the
// microphone, and it gets worse the more people are playing: six machines calling out through six
// microphones is thirty relayed copies of somebody else's audio.
//
// **A browser's echo canceller does not fix this and is not meant to.** AEC is trained on the
// *far end's* signal — what it plays back for you — so it can subtract other players' voices from
// your microphone. Your own machine's audio is near-end sound the canceller has never been shown;
// to it, that is you, talking.
//
// WHY A GATE RATHER THAN A DUCK
// -----------------------------
// Every other rule in 07 §7 is a dB offset, because a bus that is quiet is still there and a
// player expects to hear a music bed under a callout. This one is a hard on/off, because a
// *quieter* copy of the machine leaking through five microphones is still five copies of the
// machine — attenuation makes the artefact softer without making it stop being an artefact.
//
// **Nothing here says anything to the player, deliberately.** The gate opens and closes a couple
// of times a ball, for a second or so at a time; a mute indicator flickering on and off at that
// rate would be far more alarming than the thing it was reporting, and a player who speaks over
// their own machine's callout was competing with it anyway.
//
// **This is not a mute and must not be stored as one.** `voiceMute.ts` holds decisions the player
// made and remembers them across sessions; this is a transient state of the room's audio, and the
// transport combines the two — the microphone is live only when the player has not muted it *and*
// the gate is open. Conflating them would either persist a gate that closed as the page unloaded,
// or quietly un-mute somebody when a callout finished.

import { DUCK_RELEASE } from './mixRules'

/**
 * How long the microphone stays shut after the machine stops talking, seconds.
 *
 * 0.35 — longer than the 0.25 s release the speech duck uses (`DUCK_RELEASE`), and longer on
 * purpose. The duck is finished when the line is finished; the gate has two extra things to
 * outlast. **The cabinet's own reverb tail** carries the last syllable past the end of the
 * callout, and **a browser's echo canceller runs on a delay** — it holds a window of recent audio
 * and reports it late, so re-opening on the exact sample the speech ended sends the tail of it
 * anyway. A tenth of a second of margin costs a player nothing they would notice and is the
 * difference between the artefact being gone and being nearly gone.
 */
export const GATE_RELEASE_SECONDS = DUCK_RELEASE + 0.1

/**
 * The gate itself.
 *
 * **Shuts on the same frame speech starts, with no attack ramp**, which is the other way round
 * from the duck. A duck that arrived instantly would be an audible step in a music bed; a gate is
 * a boolean on a `MediaStreamTrack`, nobody hears it change, and any delay at all is exactly the
 * first syllable this exists to stop escaping.
 *
 * Advanced by frame, like everything else that has to agree with a render loop. No clock of its
 * own: the caller already has one, and a second one is a second answer to "when".
 */
export class SpeechGate {
  private heldFor = Number.POSITIVE_INFINITY

  /**
   * One frame. `speaking` is whether the machine is mid-callout *now*.
   *
   * Answers whether the microphone should be shut. Idempotent and cheap enough to call every
   * frame, which is what the caller does — the transport only hears about it when it changes.
   */
  advance(speaking: boolean, frameSeconds: number): boolean {
    if (speaking) {
      this.heldFor = 0
      return true
    }
    // Guarded against a caller that hands over a nonsense frame — a tab that was backgrounded for
    // a minute reports one enormous step, and `NaN` here would latch the gate shut for the rest of
    // the session. Both cases release, because the machine is not speaking.
    if (!Number.isFinite(frameSeconds) || frameSeconds < 0) {
      this.heldFor = Number.POSITIVE_INFINITY
      return false
    }
    this.heldFor += frameSeconds
    return this.heldFor < GATE_RELEASE_SECONDS
  }

  /** Is the microphone shut right now? What the transport is told. */
  get closed(): boolean {
    return this.heldFor < GATE_RELEASE_SECONDS
  }

  /**
   * Open it and forget the hold.
   *
   * For a machine going away, not for a callout ending. A gate left shut by a runtime that was
   * disposed mid-line would leave the player mute in a room they are still standing in.
   */
  release(): void {
    this.heldFor = Number.POSITIVE_INFINITY
  }
}
